import React, { useMemo, useState } from 'react';
import { Empty, List, Segmented, Space, Spin, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import type { ReviewsProps } from '../../../api/Reviews';

const { Text, Paragraph } = Typography;

type Props = {
  article: string;
  items: ReviewsProps[];
  total: number;
  pending?: boolean;
};

export const ReviewsTab = ({ article, items, total, pending }: Props) => {
  const [mp, setMp] = useState<string | 'all'>('all');

  const marketplaces = useMemo(() => {
    return [...new Set(items.map((i) => i.marketplace).filter(Boolean))];
  }, [items]);

  const filtered =
    mp === 'all' ? items : items.filter((i) => i.marketplace === mp);

  const withText = filtered.filter((i) => {
    const t = i.text;
    return Boolean(
      t?.comment?.trim() || t?.positive?.trim() || t?.negative?.trim(),
    );
  });
  const withoutText = filtered.length - withText.length;

  const textOf = (i: ReviewsProps) => {
    if (i.text?.comment?.trim()) return i.text.comment.trim();
    return [i.text?.positive, i.text?.negative].filter(Boolean).join(' / ');
  };

  return (
    <div>
      <Space
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <Segmented
          value={mp}
          onChange={(v) => setMp(v as string)}
          options={[
            { label: 'Все', value: 'all' },
            ...marketplaces.map((m) => ({ label: m, value: m })),
          ]}
        />
        <Link to={`/answers?article=${encodeURIComponent(article)}`}>
          Открыть очередь отзывов →
        </Link>
      </Space>

      {pending ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : !filtered.length ? (
        <Empty description="Нет отзывов" />
      ) : (
        <>
          <List
            itemLayout="vertical"
            dataSource={withText.slice(0, 15)}
            renderItem={(item) => (
              <List.Item>
                <Space wrap size={[8, 4]}>
                  <Tag>{item.marketplace}</Tag>
                  <Tag color={item.rating >= 4 ? 'success' : 'warning'}>
                    {item.rating}★
                  </Tag>
                  <Text type="secondary">
                    {item.published_at
                      ? new Date(item.published_at).toLocaleDateString('ru')
                      : ''}
                  </Text>
                  {item.answer?.text ? (
                    <Tag color="processing">Есть ответ</Tag>
                  ) : (
                    <Tag>Без ответа</Tag>
                  )}
                </Space>
                <Paragraph
                  style={{ margin: '8px 0 0' }}
                  ellipsis={{ rows: 2 }}
                >
                  {textOf(item) || 'Без текста'}
                </Paragraph>
              </List.Item>
            )}
          />
          {withoutText > 0 && (
            <Text type="secondary">
              Ещё {withoutText} отзыв(ов) без комментария
              {total > filtered.length ? ` · всего ${total}` : ''}
            </Text>
          )}
        </>
      )}
    </div>
  );
};
